import { useState } from "react";
import { AreaChart, Area, ResponsiveContainer, XAxis, Tooltip } from "recharts";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, Sparkles } from "lucide-react";
import { RevealOnScroll } from "./RevealText";
import { reportPreviewData } from "../data/staticData";
import { useLanguage } from "../i18n/LanguageContext";

const chartBlock = (
  <div className="h-44 min-h-[176px] w-full -mx-2">
    <ResponsiveContainer width="100%" height={176} minWidth={0}>
      <AreaChart data={reportPreviewData.chart} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
        <defs>
          <linearGradient id="emotionGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#00F0FF" stopOpacity={0.35} />
            <stop offset="100%" stopColor="#00F0FF" stopOpacity={0} />
          </linearGradient>
          <linearGradient id="trustGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#00FF66" stopOpacity={0.25} />
            <stop offset="100%" stopColor="#00FF66" stopOpacity={0} />
          </linearGradient>
        </defs>
        <XAxis dataKey="t" hide />
        <Tooltip
          contentStyle={{ background: "#0F0F11", border: "1px solid #27272A", borderRadius: 8, fontSize: 12 }}
          labelStyle={{ color: "#71717A" }}
        />
        <Area type="monotone" dataKey="emotion" stroke="#00F0FF" strokeWidth={2} fill="url(#emotionGrad)" />
        <Area type="monotone" dataKey="trust" stroke="#00FF66" strokeWidth={2} fill="url(#trustGrad)" />
      </AreaChart>
    </ResponsiveContainer>
  </div>
);

export function ReportPreview() {
  const { t } = useLanguage();
  const r = t.reports;
  const [page, setPage] = useState(0);

  return (
    <section id="reports" className="relative py-24 lg:py-32 bg-[#050505]" data-testid="reports-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-16 items-center">
        <div className="lg:col-span-5">
          <RevealOnScroll>
            <span className="font-mono-data text-xs text-[#00F0FF]">/ {t.nav.reports.toUpperCase()}</span>
            <h2 className="mt-4 font-display font-bold text-4xl sm:text-5xl text-white tracking-tighter leading-[1.05]">
              {r.title}
            </h2>
            <p className="mt-5 text-zinc-400 text-base sm:text-lg leading-relaxed">{r.subtitle}</p>

            <h3 className="mt-10 font-mono-data text-xs text-zinc-500 uppercase">{r.includesTitle}</h3>
            <ul className="mt-4 space-y-3">
              {r.includes.map((item) => (
                <li key={item} className="flex items-start gap-3 text-sm text-zinc-300">
                  <CheckCircle2 className="w-4 h-4 text-[#00FF66] mt-0.5 shrink-0" strokeWidth={1.5} />
                  {item}
                </li>
              ))}
            </ul>
          </RevealOnScroll>
        </div>

        <div className="lg:col-span-7">
          <RevealOnScroll delay={0.15}>
            <div
              className="relative rounded-2xl border border-white/10 bg-[#0A0A0C] p-6 sm:p-8 scan-lines"
              data-testid="report-preview-card"
            >
              <div className="flex items-center justify-between border-b border-white/10 pb-5">
                <div>
                  <p className="font-display font-semibold text-white text-sm sm:text-base">{r.previewTitle}</p>
                  <p className="font-mono-data text-xs text-zinc-500 mt-1">{reportPreviewData.date}</p>
                </div>
                <span className="font-mono-data text-[10px] px-2.5 py-1 rounded border border-[#00FF66]/30 text-[#00FF66] bg-[#00FF66]/5">
                  {reportPreviewData.statusComplete}
                </span>
              </div>

              <div className="flex items-center gap-1 mt-5 border-b border-white/10 pb-0">
                {r.pages.map((label, i) => (
                  <button
                    key={label}
                    onClick={() => setPage(i)}
                    data-testid={`report-tab-${i}`}
                    className={`px-3 py-2.5 text-xs sm:text-sm font-medium border-b-2 transition-colors duration-200 ${
                      page === i
                        ? "border-[#00F0FF] text-white"
                        : "border-transparent text-zinc-500 hover:text-zinc-300"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>

              <AnimatePresence mode="wait">
                <motion.div
                  key={page}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.3 }}
                  data-testid={`report-page-${page}`}
                >
                  {page === 0 && (
                    <>
                      <div className="grid grid-cols-3 gap-4 py-6">
                        {reportPreviewData.metricValues.map((m, i) => (
                          <div key={r.metricLabels[i]}>
                            <p className="font-mono-data text-2xl sm:text-3xl font-bold text-white">
                              {m.value}
                              <span className="text-sm text-zinc-500">{m.unit}</span>
                            </p>
                            <p className="text-xs text-zinc-500 mt-1 uppercase font-mono-data">
                              {r.metricLabels[i]}
                            </p>
                          </div>
                        ))}
                      </div>
                      {chartBlock}
                    </>
                  )}

                  {page === 1 && (
                    <div className="py-6">
                      {chartBlock}
                      <div className="mt-4 border-t border-white/10 pt-5 space-y-3">
                        {r.keyMoments.map((k, i) => (
                          <div key={k.label} className="flex items-start gap-4 text-sm">
                            <span className="font-mono-data text-xs text-[#00F0FF] w-12 shrink-0 pt-0.5">
                              {reportPreviewData.keyMomentTimes[i]}
                            </span>
                            <div>
                              <p className="text-white font-medium">{k.label}</p>
                              <p className="text-zinc-500 text-xs mt-0.5">{k.detail}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {page === 2 && (
                    <div className="py-6">
                      <div className="grid grid-cols-3 gap-4 mb-6">
                        {reportPreviewData.metricValues.map((m, i) => (
                          <div key={r.metricLabels[i]}>
                            <p className="font-mono-data text-xl sm:text-2xl font-bold text-white">
                              {m.value}
                              <span className="text-xs text-zinc-500">{m.unit}</span>
                            </p>
                            <p className="text-[10px] text-zinc-500 mt-1 uppercase font-mono-data">
                              {r.metricLabels[i]}
                            </p>
                            <div className="mt-2 h-1 rounded-full bg-white/10 overflow-hidden">
                              <div
                                className="h-full bg-[#00F0FF]"
                                style={{ width: `${parseFloat(m.value) <= 10 ? parseFloat(m.value) * 10 : parseFloat(m.value)}%` }}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                      <h4 className="font-mono-data text-xs text-zinc-500 uppercase flex items-center gap-2">
                        <Sparkles className="w-3.5 h-3.5 text-[#00F0FF]" /> {r.recommendationsTitle}
                      </h4>
                      <ul className="mt-3 space-y-3">
                        {r.recommendations.map((rec) => (
                          <li key={rec} className="flex items-start gap-3 text-sm text-zinc-300 leading-relaxed">
                            <CheckCircle2 className="w-4 h-4 text-[#00FF66] mt-0.5 shrink-0" strokeWidth={1.5} />
                            {rec}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </motion.div>
              </AnimatePresence>
            </div>
          </RevealOnScroll>
        </div>
      </div>
    </section>
  );
}
